import VAddUser from '@/presentation/views/users/VAddUser.vue'
import VQuizEdit from '@/presentation/views/quiz/VQuizEdit.vue'
import {
  VLearningCourse,
  VLearningCourseAdd,
  VLearningCoursesList,
  VLearningCourseUpdate
} from '@/presentation/views/learningCourses'
import type { RouteLocationNormalized } from 'vue-router'
import VDashboardSchoolList from '@/presentation/views/dashboard/VDashboardSchoolList.vue'
import VDashboardClassroom from '@/presentation/views/dashboard/VDashboardClassroom.vue'
import VDashboardGroupProgress from '@/presentation/views/dashboard/VDashboardClassroomProgress.vue'
import VDashboard from '@/presentation/views/dashboard/VDashboard.vue'
import type { App } from 'vue'
import VNabooContentList from '@/presentation/views/nabooContent/VNabooContentList.vue'
import VNabooContentModule from '@/presentation/views/nabooContent/VNabooContentModule.vue'
import VSchools from '@/presentation/views/schools/VSchools.vue'
import VSchoolsList from '@/presentation/views/schools/VSchoolsList.vue'
import VSchoolsSchool from '@/presentation/views/schools/VSchoolsSchool.vue'
import VSchoolsUpdate from '@/presentation/views/schools/VSchoolsUpdate.vue'
import { authGuard } from '@auth0/auth0-vue'
import VLearningModuleUpdate from '@/presentation/views/modules/VLearningModuleUpdate.vue'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import VEMethodos from '@/presentation/views/emethodos/VEMethodos.vue'
import VEMethodosList from '@/presentation/views/emethodos/VEMethodosList.vue'
import VExports from '@/presentation/views/exports/VExports.vue'
import VExportsCore from '@/presentation/views/exports/VExportsCore.vue'
import VUsers from '@/presentation/views/users/VUsers.vue'
import VUsersCore from '@/presentation/views/users/VUsersCore.vue'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'

export const createNabooAdminRoutes = (app?: App) => {
  return [
    {
      path: '/tableau-de-bord',
      alias: ['/'],
      component: VDashboard,
      redirect: '/tableau-de-bord',
      meta: {
        group: 'dashboard',
        icon: 'pi-home',
        menuTitle: 'Tableau de bord',
        menuOrder: 1
      },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: VDashboardSchoolList,
          beforeEnter: [authGuard],
          meta: {
            group: 'dashboard',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN, UserRole.Values.PERDIR, UserRole.Values.CPE, UserRole.Values.TEACHER]
          }
        },
        {
          path: 'etablissements/:schoolId(\\d+)/classe/:classroomId(\\d+)',
          children: [
            {
              path: '',
              name: 'dashboard-group',
              component: VDashboardClassroom,
              beforeEnter: [authGuard],
              props: (route: RouteLocationNormalized) => ({
                schoolId: Number(route.params.schoolId),
                classroomId: Number(route.params.classroomId)
              }),
              meta: {
                group: 'dashboard',
                withSidebar: true,
                roles: [UserRole.Values.ADMIN, UserRole.Values.PERDIR, UserRole.Values.CPE, UserRole.Values.TEACHER]
              }
            },
            {
              path: 'resultats',
              name: 'dashboard-group-progress',
              component: VDashboardGroupProgress,
              beforeEnter: [authGuard],
              props: (route: RouteLocationNormalized) => ({
                schoolId: Number(route.params.schoolId),
                classroomId: Number(route.params.classroomId)
              }),
              meta: {
                group: 'dashboard',
                withSidebar: true,
                roles: [UserRole.Values.ADMIN, UserRole.Values.PERDIR, UserRole.Values.CPE, UserRole.Values.TEACHER]
              }
            }
          ]
        }
      ]
    },
    {
      path: '/utilisateurs',
      beforeEnter: [authGuard],
      component: VUsers,
      meta: {
        group: 'users',
        icon: 'pi-user',
        menuTitle: 'Utilisateurs',
        menuOrder: 2
      },
      children: [
        {
          path: '',
          name: 'users-list',
          component: VUsersCore,
          beforeEnter: [authGuard],
          meta: {
            group: 'users',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        },
        {
          path: 'ajouter',
          name: 'create-user',
          component: VAddUser,
          beforeEnter: [authGuard],
          meta: {
            group: 'users',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        },
        {
          path: 'modifier/:identifier',
          name: 'edit-user',
          component: VAddUser,
          beforeEnter: [authGuard],
          meta: {
            group: 'users',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        }
      ]
    },
    {
      path: '/parcours',
      beforeEnter: [authGuard],
      meta: {
        group: 'learning-courses',
        icon: 'pi-th-large',
        menuTitle: 'Parcours',
        menuOrder: 3
      },
      children: [
        {
          path: '',
          name: 'learning-courses-list',
          component: VLearningCoursesList,
          beforeEnter: [authGuard],
          meta: {
            group: 'learning-courses',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
          }
        },
        {
          path: 'ajouter',
          name: 'learning-courses-add',
          component: VLearningCourseAdd,
          beforeEnter: [authGuard],
          meta: {
            group: 'learning-courses',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
          }
        },
        {
          path: ':courseId(\\d+)',
          component: VLearningCourse,
          beforeEnter: [authGuard],
          props: (route: RouteLocationNormalized) => ({ courseId: Number(route.params.courseId) }),
          children: [
            {
              path: 'modifier',
              name: 'learning-course-update',
              component: VLearningCourseUpdate,
              beforeEnter: [authGuard],
              props: (route: RouteLocationNormalized) => ({
                courseId: Number(route.params.courseId)
              }),
              meta: {
                group: 'learning-courses',
                withSidebar: true,
                roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
              }
            },
            {
              path: 'quiz/:quizId(\\d+)',
              name: 'learning-course-update-quiz',
              component: VQuizEdit,
              beforeEnter: [authGuard],
              props: (route: RouteLocationNormalized) => ({
                courseId: Number(route.params.courseId),
                quizId: Number(route.params.quizId),
                taskId: Number(route.query.taskId)
              }),
              meta: {
                group: 'learning-courses',
                withSidebar: true,
                roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
              }
            },
            {
              path: 'modules',
              children: [
                {
                  path: 'ajouter',
                  name: 'learning-course-add-module',
                  component: VLearningModuleUpdate,
                  beforeEnter: [authGuard],
                  meta: {
                    group: 'learning-courses',
                    withSidebar: true,
                    roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
                  }
                },
                {
                  path: ':moduleId(\\d+)',
                  children: [
                    {
                      path: '',
                      name: 'learning-course-update-module',
                      component: VLearningModuleUpdate,
                      beforeEnter: [authGuard],
                      props: (route: RouteLocationNormalized) => ({
                        courseId: Number(route.params.courseId),
                        moduleId: Number(route.params.moduleId)
                      }),
                      meta: {
                        group: 'learning-courses',
                        withSidebar: true,
                        roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
                      }
                    },
                    {
                      path: 'grain/:grainId(\\d+)/quiz/:quizId(\\d+)',
                      name: 'learning-grain-update-quiz',
                      component: VQuizEdit,
                      beforeEnter: [authGuard],
                      props: (route: RouteLocationNormalized) => ({
                        courseId: Number(route.params.courseId),
                        moduleId: Number(route.params.moduleId),
                        grainId: Number(route.params.grainId),
                        quizId: Number(route.params.quizId)
                      }),
                      meta: {
                        group: 'learning-courses',
                        withSidebar: true,
                        roles: [UserRole.Values.ADMIN, UserRole.Values.EDU_ENGINEER]
                      }
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/etablissements',
      beforeEnter: [authGuard],
      component: VSchools,
      meta: {
        group: 'schools',
        icon: 'pi-building',
        menuTitle: 'Établissements',
        menuOrder: 5
      },
      children: [
        {
          path: 'liste',
          name: 'schools-list',
          component: VSchoolsList,
          beforeEnter: [authGuard],
          meta: {
            group: 'schools',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        },
        {
          path: 'creer-un-etablissement',
          name: 'schools-create',
          component: VSchoolsUpdate,
          beforeEnter: [authGuard],
          meta: {
            group: 'schools',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN],
            showBreadCrumbs: true
          }
        },
        {
          path: 'mettre-a-jour-un-etablissement/:schoolSlug',
          name: 'schools-update',
          component: VSchoolsUpdate,
          beforeEnter: [authGuard],
          meta: {
            group: 'schools',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN],
            showBreadCrumbs: true
          },
          props: (route: RouteLocationNormalized) => ({ schoolSlug: route.params.schoolSlug })
        },
        {
          path: ':schoolSlug',
          name: 'school',
          component: VSchoolsSchool,
          beforeEnter: [authGuard, async (to, from, next) => {
            const schoolSlug = to.params.schoolSlug as string
            const schoolStore = useSchoolStore()
            const schoolPeriodStore = useSchoolPeriodStore()

            try {
              // Récupérer l'école par son slug
              const school = schoolStore.getSchoolBySlug(schoolSlug)

              if (!school) {
                // Si l'école n'est pas trouvée, continuer quand même
                // L'erreur sera gérée par le composant
                next()
                return
              }

              // Sélectionner l'école
              schoolStore.selectSchool(school)

              // Charger les périodes scolaires (force refresh)
              await schoolPeriodStore.fetchAllSchoolPeriodsForSchool(school.id, true)

              next()
            } catch (error) {
              // En cas d'erreur, autoriser l'accès mais l'erreur sera affichée
              console.error('Erreur lors du chargement des périodes scolaires:', error)
              next()
            }
          }],
          props: (route: RouteLocationNormalized) => ({ schoolSlug: route.params.schoolSlug }),
          meta: {
            group: 'schools',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN],
            showBreadCrumbs: true
          }
        }
      ]
    },
    {
      path: '/contenus-naboo',
      beforeEnter: [authGuard],
      meta: {
        group: 'naboo-content',
        icon: 'pi-box',
        menuTitle: 'Contenus Naboo',
        menuOrder: 4
      },
      children: [
        {
          path: '',
          name: 'naboo-content-list',
          component: VNabooContentList,
          beforeEnter: [authGuard],
          meta: {
            group: 'naboo-content',
            withSidebar: true,
            roles: [
              UserRole.Values.ADMIN,
              UserRole.Values.EDU_ENGINEER,
              UserRole.Values.PERDIR,
              UserRole.Values.CPE,
              UserRole.Values.TEACHER
            ]
          }
        },
        {
          path: '/contenus-naboo/parcours/:courseId(\\d+)/modules/:moduleId(\\d+)',
          name: 'naboo-content-module',
          component: VNabooContentModule,
          beforeEnter: [authGuard],
          props: (route: RouteLocationNormalized) => ({
            courseId: Number(route.params.courseId),
            moduleId: Number(route.params.moduleId)
          }),
          meta: {
            group: 'naboo-content',
            withSidebar: true,
            roles: [
              UserRole.Values.ADMIN,
              UserRole.Values.EDU_ENGINEER,
              UserRole.Values.PERDIR,
              UserRole.Values.CPE,
              UserRole.Values.TEACHER
            ]
          }
        }
      ]
    },
    {
      path: '/e-methodos',
      beforeEnter: [authGuard],
      component: VEMethodos,
      meta: {
        group: 'e-methodos',
        icon: 'pi-video',
        menuTitle: 'E-Methodos',
        menuOrder: 6
      },
      children: [
        {
          path: 'liste',
          name: 'emethodos-list',
          component: VEMethodosList,
          beforeEnter: [authGuard],
          meta: {
            group: 'e-methodos',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        }
      ]
    },
    {
      path: '/exports',
      beforeEnter: [authGuard],
      component: VExports,
      meta: {
        group: 'exports',
        icon: 'pi-file-export',
        menuTitle: 'Export des données',
        menuOrder: 7
      },
      children: [
        {
          path: '',
          name: 'exports',
          component: VExportsCore,
          beforeEnter: [authGuard],
          meta: {
            group: 'exports',
            withSidebar: true,
            roles: [UserRole.Values.ADMIN]
          }
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*', // Route catch-all
      name: 'NotFound',
      redirect: '/'
    }
  ]
}
